import moment from 'moment';
import { APIError } from '../APIError';
import {
  ActiveSearch,
  Export,
  ExportStatus,
  Histogram,
  RawExport,
  Search,
  SearchResults,
} from '../types';

type RawSearch = {
  id: string;
  request: {
    start_time: string;
    end_time: string;
    query: string;
    case_sensitive: boolean;
    log_sources: Array<string>;
    log_sources_tags: {
      [key: string]: Array<string>;
    };
  };
  failed: boolean;
  user: string;
  date: string;
  percent: number;
  count: number;
  size: number;
  exports: Array<RawExport>;
};

type RawSearchHistogram = {
  search: RawSearch & {
    available_count: number;
  };
  histogram: Histogram;
};

type NewSearchAction = {
  readonly type: 'start_new_search';
  clearRequest: boolean;
};

type SetEarliestRecordAction = {
  readonly type: 'set_log_earliest_record';
  earliest: moment.Moment;
};

type SetResultPageAction = {
  readonly type: 'set_log_search_result_page';
  searchId: string;
  resultPage: SearchResults;
};

type SetSearchErrorAction = {
  readonly type: 'set_log_search_error';
  error: APIError | null;
};

type SetSearchAction = ActiveSearch & {
  readonly type: 'set_log_search';
};

type SetSearchIdAction = {
  readonly type: 'set_log_search_id';
  searchId: string;
};

type SetSearchesListAction = {
  readonly type: 'set_log_searches';
  searches: Array<Search>;
};

type SetSearchesListErrorAction = {
  readonly type: 'set_log_search_list_error';
  error: APIError | null;
};

type SetSearchesLoadingAction = {
  readonly type: 'set_log_searches_loading';
};

type SetSearchesFiltersAction = {
  readonly type: 'set_log_searches_filters';
  readonly filters: URLSearchParams;
};

type SetExportAction = {
  readonly type: 'set_log_search_export';
  export: Export;
};

export type LogSearchAction =
  | NewSearchAction
  | SetEarliestRecordAction
  | SetResultPageAction
  | SetSearchAction
  | SetSearchErrorAction
  | SetSearchIdAction
  | SetSearchesListAction
  | SetSearchesListErrorAction
  | SetSearchesLoadingAction
  | SetSearchesFiltersAction
  | SetExportAction;

export const LOG_SEARCH_ACTIONS = {
  CLEAR_SEARCH_EXCEPT_REQUEST: (): NewSearchAction => ({
    type: 'start_new_search',
    clearRequest: false,
  }),
  NEW_SEARCH: (): NewSearchAction => ({
    type: 'start_new_search',
    clearRequest: true,
  }),
  SET_EARLIEST_RECORD: (earliestRecordObject: { earliest: string }) => ({
    type: 'set_log_earliest_record',
    earliest: moment(earliestRecordObject.earliest),
  }),
  SET_ERROR: (error: APIError | null): SetSearchErrorAction => ({
    type: 'set_log_search_error',
    error,
  }),
  SET_LIST_LOADING: (): SetSearchesLoadingAction => ({
    type: 'set_log_searches_loading',
  }),
  SET_RESULT_PAGE: (
    searchId: string,
    resultPage: SearchResults,
  ): SetResultPageAction => ({
    type: 'set_log_search_result_page',
    searchId,
    resultPage,
  }),
  SET_SEARCH: (rawSearchHistogram: RawSearchHistogram): SetSearchAction => {
    const { search, histogram } = rawSearchHistogram;
    return {
      type: 'set_log_search',
      search: {
        id: search.id,
        request: {
          query: search.request.query,
          start: moment.utc(search.request.start_time),
          end: moment.utc(search.request.end_time),
          caseSensitive: search.request.case_sensitive,
          logSourcesTags: search.request.log_sources_tags,
        },
        failed: search.failed,
        date: moment.utc(search.date),
        count: search.count,
        availableCount: search.available_count,
        user: search.user,
        percent: search.percent,
        size: search.size,
        searchExport: (search.exports || []).length
          ? {
              id: search.exports[0].export_id,
              searchId: search.exports[0].search_id,
              percentComplete: search.exports[0].percent_complete,
              status: search.exports[0].status || ExportStatus.COMPLETE,
            }
          : null,
      },
      histogram,
    };
  },
  SET_SEARCH_ID: (searchId: string): SetSearchIdAction => ({
    type: 'set_log_search_id',
    searchId,
  }),
  SET_SEARCH_LIST_ERROR: (
    error: APIError | null,
  ): SetSearchesListErrorAction => ({
    type: 'set_log_search_list_error',
    error,
  }),
  SET_SEARCHES_LIST: (rawSearches: Array<RawSearch>): SetSearchesListAction => {
    const searches: Array<Search> = rawSearches.map((raw) => ({
      id: raw.id,
      request: {
        query: raw.request.query,
        start: moment.utc(raw.request.start_time),
        end: moment.utc(raw.request.end_time),
        caseSensitive: raw.request.case_sensitive,
        logSourcesTags: raw.request.log_sources_tags,
      },
      failed: raw.failed,
      user: raw.user,
      date: moment.utc(raw.date),
      count: raw.count,
      availableCount: raw.count, // list api do not have available_count
      percent: raw.percent,
      size: raw.size,
      searchExport: (raw.exports || []).length
        ? {
            id: raw.exports[0].export_id,
            searchId: raw.exports[0].search_id,
            percentComplete: raw.exports[0].percent_complete,
            status: raw.exports[0].status,
          }
        : null,
    }));
    return { type: 'set_log_searches', searches };
  },
  SET_SEARCHES_FILTERS: (
    filters: URLSearchParams,
  ): SetSearchesFiltersAction => ({
    type: 'set_log_searches_filters',
    filters,
  }),
  SET_EXPORT: (searchExport: RawExport): SetExportAction => ({
    type: 'set_log_search_export',
    export: {
      id: searchExport.export_id,
      searchId: searchExport.search_id,
      percentComplete: searchExport.percent_complete,
      status: searchExport.status,
    },
  }),
};
